import { useMemo } from 'react';

import { HubConnectionState } from '@microsoft/signalr';
import { useCurrentSiteConnection } from '@realTimeDataAccess';
import { TrafficLightType } from '@scaleaq/scaleui';
import { useTranslation } from 'react-i18next';

import { useGetSiteInventoryHealth } from '@/dataAccess/rest';
import { useSiteContext } from '@/providers';
import { WidgetHealthIssue } from '@/types.ts';
import { widgetHealthSeverityToTrafficLightType } from '@/utils';

import { useConfigStore } from '../../stores';
import {
  getConnectionStateText,
  getConnectionTrafficLightType,
  getTranslatedConnectionState,
} from './useSiteNameAndConnectionState/';

type UseSiteNameAndConnectionStateProps = {
  healthIssues: WidgetHealthIssue[];
  cloudBacked?: boolean;
};

export const useSiteNameAndConnectionState = ({
  healthIssues,
  cloudBacked = false,
}: UseSiteNameAndConnectionStateProps) => {
  const { t } = useTranslation(['common']);
  const portableMode = useConfigStore(s => s.portableMode);
  // Cloud-backed widgets show reduced info in portable mode ...
  const reduceInfo = cloudBacked && portableMode;
  const { connection: siteConnection, isError: isClosed } = useCurrentSiteConnection();
  const { currentSite, sites } = useSiteContext();

  const isMultiSite = useMemo(() => sites.length > 1, [sites.length]);
  const safeSiteName = currentSite?.name ?? t('notAvailable');

  // ... and always follow the cloud inventory probe, also on an edge cluster.
  const cloudHealth = useGetSiteInventoryHealth(currentSite, cloudBacked, true);

  const trafficLightType: TrafficLightType = useMemo(() => {
    const topIssue = healthIssues?.[0];
    if (topIssue) return widgetHealthSeverityToTrafficLightType[topIssue.severity];
    if (cloudBacked && cloudHealth) {
      return cloudHealth === 'healthy' ? 'success' : 'alarm';
    }
    return getConnectionTrafficLightType(siteConnection.state, isClosed);
  }, [healthIssues, siteConnection.state, isClosed, cloudBacked, cloudHealth]);

  const displayText = useMemo(() => {
    if (reduceInfo) return '';

    const siteName = isMultiSite ? currentSite?.name : '';
    if (isClosed) return t('hubConnection.connectionClosed', { siteName: safeSiteName });
    if (siteConnection.state === HubConnectionState.Connected) return siteName;
    if (!siteName) return getTranslatedConnectionState(siteConnection.state, t);

    return getConnectionStateText({
      connectionState: siteConnection.state,
      siteName,
      t,
    });
  }, [isMultiSite, currentSite?.name, siteConnection.state, isClosed, t, safeSiteName, reduceInfo]);

  const tooltipText = useMemo(() => {
    if (healthIssues.length > 0) return healthIssues.map(issue => issue.message).join('\n');
    if (reduceInfo) return safeSiteName;
    if (isClosed) return t('hubConnection.connectionClosed', { siteName: safeSiteName });

    return getConnectionStateText({
      connectionState: siteConnection.state,
      siteName: safeSiteName,
      t,
    });
  }, [healthIssues, isClosed, siteConnection.state, safeSiteName, t, reduceInfo]);

  return {
    trafficLightType,
    displayText,
    tooltipText,
  };
};
